import {Paper, Title, Text, Stack, Group, SimpleGrid, Card, ThemeIcon, Image, Flex, Alert, Divider} from "@mantine/core";
import {
    CursorClickIcon,
    LightningIcon,
    ChartBarIcon,
    CalculatorIcon,
    PushPinIcon,
    PaintBrushIcon,
    MagnifyingGlassIcon,
    SlidersIcon,
    WarningCircleIcon,
    ShieldWarningIcon,
} from "@phosphor-icons/react";
import Page from "../../components/Page/Page";


const MediaFrame = ({children}) => (
    <Paper withBorder radius="md" p={6} shadow="sm"
           style={{width: '100%', overflow: 'hidden', display: 'flex', justifyContent: 'center'}}>
        {children}
    </Paper>
);

const Screenshot = ({src, alt}) => (
    <MediaFrame>
        <Image src={src} alt={alt} w="auto" maw="100%" radius="sm" fit="contain"/>
    </MediaFrame>
);


const SHOWCASE = [
    {
        icon: <CursorClickIcon size={26}/>,
        title: 'Effortless Entity Selection',
        media: <Screenshot src="https://assets.swc-forge.com/browser-extension/selection.png" alt="Selecting a precise number of entities"/>,
        text: "Select a specific number of entities in any situation — from the inventory page to equipment, party, and cargo screens. No more tedious clicking, just quick, precise selection every time. And no more counting in your head!",
    },
    {
        icon: <LightningIcon size={26}/>,
        title: 'Quick Action Buttons',
        media: <Screenshot src="https://assets.swc-forge.com/browser-extension/quick-actions.png" alt="Quick action buttons in the inventory"/>,
        text: 'Speed up your gameplay with handy quick action buttons. Drop your equipment, move your party, and perform other common actions with a single click. Efficiency at its best!',
    },
    {
        icon: <ChartBarIcon size={26}/>,
        title: 'Combat Event Totals',
        media: <Screenshot src="https://assets.swc-forge.com/browser-extension/report-totals.png" alt="Combat report totals for both sides"/>,
        text: 'Get the total damage for both sides in combat reports, including hit ratios — so you can read the outcome of an engagement at a glance instead of adding it up yourself.',
    },
    {
        icon: <CalculatorIcon size={26}/>,
        title: 'Smart Amount Parser',
        media: (
            <MediaFrame>
                <video
                    src="https://assets.swc-forge.com/browser-extension/amount-parser.webm"
                    autoPlay
                    loop
                    muted
                    playsInline
                    style={{width: '100%', borderRadius: '6px', display: 'block'}}
                />
            </MediaFrame>
        ),
        text: 'Typed one zero too many when sending 100M and found yourself poor? The extension automatically adds thousands separators to keep numbers readable. You can also type "5m" or "1.2b" as shortcuts to quickly input large numbers.',
    },
];

const MORE_FEATURES = [
    {
        icon: <PushPinIcon size={22}/>,
        title: 'Sticky Inventory Action Bar',
        text: 'Tired of scrolling down to reach inventory actions? The action bar stays pinned to the bottom of the screen, always visible and ready.',
    },
    {
        icon: <PaintBrushIcon size={22}/>,
        title: 'Enhanced Equipment Visibility',
        text: 'Never lose track of your gear again. Equipment owned by you is colored in the equipment screen, making it easy to spot and manage.',
    },
    {
        icon: <MagnifyingGlassIcon size={22}/>,
        title: 'Jump to Entity',
        text: 'A quick shortcut to access a specific entity from your inventories directly by its id.',
    },
    {
        icon: <SlidersIcon size={22}/>,
        title: 'Fully Customizable',
        text: 'Every feature can be enabled or disabled independently from the extension options.',
    },
];


const StoreBadges = () => (
    <Group justify="center" gap="xl" wrap="wrap">
        <a href="https://chromewebstore.google.com/detail/the-forge/gblolkcpiahefkcdmbcbhjphpenmkdge?authuser=0&hl=en-GB"
           target="_blank" rel="noreferrer">
            <img src="https://developer.chrome.com/static/docs/webstore/branding/image/UV4C4ybeBTsZt43U4xis.png"
                 alt="Available in the Chrome Web Store"/>
        </a>
        <a href="https://addons.mozilla.org/en-GB/firefox/addon/the-forge/" target="_blank" rel="noreferrer">
            <img src="https://blog.mozilla.org/addons/files/2015/11/get-the-addon.png" alt="Get the Firefox Add-on"/>
        </a>
    </Group>
);


export default function BrowserExtension() {
    return (
        <Page>
            <title>Browser Extension</title>

            <Stack gap="xl">
                {/* Hero */}
                <Paper shadow="xs" p="xl" withBorder>
                    <Stack gap="md">
                        <Group gap="md" wrap="nowrap" align="center">
                            <img src="/logo-100.png" alt="The Forge" width={56} height={56}/>
                            <div>
                                <Title order={1} m={0}>The Forge — Browser Extension</Title>
                                <Text c="dimmed" mt={4}>
                                    Not every tool can live on a separate web page. This extension injects directly into
                                    the SWC interface to improve usability and add brand-new features.
                                </Text>
                            </div>
                        </Group>
                        <Divider/>
                        <StoreBadges/>
                    </Stack>
                </Paper>

                {/* Highlighted features with media */}
                <Stack gap={48}>
                    {SHOWCASE.map((feature, index) => (
                        <Flex
                            key={feature.title}
                            direction={{base: 'column', md: index % 2 === 1 ? 'row-reverse' : 'row'}}
                            gap="xl"
                            align="center"
                        >
                            <div style={{flex: 1, minWidth: 0}}>
                                <Group gap="sm" mb="sm" wrap="nowrap">
                                    <ThemeIcon size={44} radius="md" variant="light" color="forge">
                                        {feature.icon}
                                    </ThemeIcon>
                                    <Title order={3} m={0}>{feature.title}</Title>
                                </Group>
                                <Text c="dimmed" fz="md">{feature.text}</Text>
                            </div>
                            <div style={{flex: 1, minWidth: 0, width: '100%'}}>
                                {feature.media}
                            </div>
                        </Flex>
                    ))}
                </Stack>

                {/* Secondary features */}
                <div>
                    <Title order={2} ta="center" mb="lg">And more</Title>
                    <SimpleGrid cols={{base: 1, sm: 2}} spacing="lg">
                        {MORE_FEATURES.map(feature => (
                            <Card key={feature.title} shadow="sm" p="lg" radius="md" withBorder h="100%">
                                <Group gap="sm" mb="xs" wrap="nowrap">
                                    <ThemeIcon size={38} radius="md" variant="light" color="forge">
                                        {feature.icon}
                                    </ThemeIcon>
                                    <Text fw={600} fz="lg">{feature.title}</Text>
                                </Group>
                                <Text c="dimmed">{feature.text}</Text>
                            </Card>
                        ))}
                    </SimpleGrid>
                </div>

                {/* Install CTA */}
                <Paper shadow="xs" p="xl" withBorder>
                    <Title order={2} ta="center" mt={0} mb="lg">Get it here!</Title>
                    <StoreBadges/>
                </Paper>

                {/* Safety notices */}
                <Stack gap="sm">
                    <Alert color="blue" icon={<WarningCircleIcon size={18}/>} title="Disclaimer">
                        A browser extension like this has complete access to the pages you visit within the SWC website.
                        This means it <b>can</b> potentially be used to spy on you. Obviously I do <b>not</b> use it for
                        this, and the extension does not communicate with any external servers.
                    </Alert>
                    <Alert color="yellow" icon={<ShieldWarningIcon size={18}/>} title="Warning">
                        Changes to the SWC interface may cause issues — please use carefully and double-check any action
                        that cannot be undone. For example, if you need to make over some entities, using the plugin to
                        assign an infotext and then selecting by that infotext is much safer.
                    </Alert>
                </Stack>
            </Stack>
        </Page>
    );
}
